<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Purchase',
  },
})

const router = useRouter()
const { loading, errors, request } = useApiRequest()

// ─── Supplier dropdown (opsional) ──────────────────────────────────────────
const suppliers = ref([])
const optLoading = ref(true)

onMounted(async () => {
  try {
    const res = await useApi('/api/v1/suppliers', { query: { per_page: 200 } }).get()
    const body = res?.data ?? res
    const list = Array.isArray(body) ? body : (body?.data ?? [])
    suppliers.value = list.map(s => ({ title: s.name, value: s.id }))
  } catch (err) {
    console.error('[ReceiptScanUpload] loadSuppliers error', err)
  } finally {
    optLoading.value = false
  }
})

const form = ref({ supplier_id: null })

// ─── Upload foto ────────────────────────────────────────────────────────────
// Sengaja pakai <input type="file"> native (bukan VFileInput) supaya atribut
// capture="environment" dijamin membuka kamera belakang HP langsung
// (sesuai instruksi Modul 23) — VFileInput Vuetify 3 tidak menjamin
// meneruskan atribut capture ke input aslinya.
const fileInputRef = ref(null)
const imageFile = ref(null)
const imagePreview = ref(null)

const openFilePicker = () => fileInputRef.value?.click()

const onImageChange = event => {
  const file = event.target?.files?.[0] ?? null

  if (!file) {
    imageFile.value = null
    imagePreview.value = null
    return
  }

  imageFile.value = file
  const reader = new FileReader()
  reader.onload = e => { imagePreview.value = e.target.result }
  reader.readAsDataURL(file)
}

const resetImage = () => {
  imageFile.value = null
  imagePreview.value = null
  if (fileInputRef.value) fileInputRef.value.value = ''
}

// ─── Submit ───────────────────────────────────────────────────────────────
const submit = async () => {
  if (!imageFile.value) return

  const fd = new FormData()
  fd.append('image', imageFile.value)
  if (form.value.supplier_id) fd.append('supplier_id', form.value.supplier_id)

  const result = await request(() =>
    useApi('/api/v1/purchase/receipt-scans').post({ body: fd })
  )

  if (result !== null) {
    const body = result?.data ?? result
    const scanId = body?.scan_id ?? body?.data?.scan_id

    router.push({ name: 'purchase-receipt-scan-id', params: { id: scanId } })
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12" md="8" offset-md="2">
      <VCard>
        <VCardTitle class="pt-5 px-5">
          Scan Struk (AI)
        </VCardTitle>

        <VDivider />

        <VCardText class="pa-5">
          <VAlert type="info" variant="tonal" class="mb-4 text-body-2">
            Foto struk belanja dari supplier — sistem akan otomatis membaca nama item, qty, satuan,
            dan harga. Anda tetap bisa mengoreksi hasilnya sebelum dijadikan Purchase Receiving.
          </VAlert>

          <VAlert
            v-if="errors._general"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ errors._general[0] }}
          </VAlert>

          <VAutocomplete
            v-model="form.supplier_id"
            :items="suppliers"
            :loading="optLoading"
            label="Supplier (opsional)"
            clearable
            no-data-text="Tidak ada supplier"
            class="mb-4"
          />

          <!-- Preview foto -->
          <div v-if="imagePreview" class="text-center mb-2">
            <VImg
              :src="imagePreview"
              max-height="420"
              class="rounded border mx-auto"
              contain
            />
            <VBtn
              variant="text"
              color="error"
              size="small"
              prepend-icon="ri-close-line"
              class="mt-2"
              @click="resetImage"
            >
              Ganti Foto
            </VBtn>
          </div>

          <!-- Area upload -->
          <template v-else>
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*"
              capture="environment"
              class="d-none"
              @change="onImageChange"
            >

            <VCard
              variant="tonal"
              class="pa-8 text-center cursor-pointer"
              style="border: 2px dashed rgba(var(--v-theme-primary), 0.4);"
              @click="openFilePicker"
            >
              <VIcon icon="ri-camera-line" size="48" class="mb-3 text-primary" />
              <div class="text-body-1 font-weight-medium mb-1">
                Ambil Foto Struk / Pilih dari Galeri
              </div>
              <div class="text-caption text-medium-emphasis">
                Maks. 5MB &middot; Format JPG, PNG, atau WEBP
              </div>
            </VCard>

            <div v-if="errors.image" class="text-error text-caption mt-2">
              {{ errors.image[0] }}
            </div>
          </template>
        </VCardText>

        <VDivider />

        <VCardActions class="pa-5">
          <VSpacer />
          <VBtn variant="tonal" :to="{ name: 'purchase-receivings' }">
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="loading"
            :disabled="!imageFile"
            prepend-icon="ri-upload-cloud-2-line"
            @click="submit"
          >
            Upload &amp; Proses
          </VBtn>
        </VCardActions>
      </VCard>
    </VCol>
  </VRow>
</template>
